import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {choose} from 'lit/directives/choose.js';
import {classMap} from 'lit/directives/class-map.js';

import {router, navigator, outlet} from 'lit-element-router';

import {configureLocalization} from '@lit/localize';

import storeEngine from 'store/src/store-engine';
import localStorage from 'store/storages/localStorage';
import cookieStorage from 'store/storages/cookieStorage';
import defaultsPlugin from 'store/plugins/defaults';

import {sourceLocale, targetLocales} from '../../../generated/locales.js'; // Generated via output.localeCodesModule
import getRoutes from '../routes';
import store from '../store';
import {colorThemes, sizes, fonts} from '../css/styleSets';

import {
	setDebug,
	setFamily,
	setLang,
	setStatus,
	setMedia,
	setKey, setRegion, setEmbed, setDebugShowAll, setDebugAeto, setOriginalKey, setInitialized, setComponentView
} from '../slices/statusSlice';
import {fetchConfiguration, setFeatureModel} from '../slices/configurationSlice';
import config from '../config';
import {routeUrl} from '../util/routeUrl';
import {generateKey} from '../util/generateKey';

import * as templatesDe from '../../../generated/locales/de.js';
import * as templatesFr from '../../../generated/locales/fr.js';

import {selectSectionConfig, selectCurrentSection, selectCurrentSectionConfig, selectFamilyConfig, selectFinalized, selectFamily} from '../selectors';
import {setLoggedIn, silentLogin} from '../slices/userSlice';
import {selectIomoEnvironment} from '../iomo/iomoSelectors';
import {startSpcSession} from '../slices/spcSlice';
import {spcExpliciteChoiceDecision} from '../util/values';
import {regionToValueMap} from '../valueMaps/regionToValueMap';

const localizedTemplates = new Map([
	['de', templatesDe],
	['fr', templatesFr],
]);

export const {getLocale, setLocale} = configureLocalization({
	sourceLocale: sourceLocale,
	targetLocales: targetLocales,
	// use static locales, because of small size
	loadLocale: async (locale) => localizedTemplates.get(locale)
	// loadLocale: (locale) => import('/locales/' + locale +'.js'),
});

@customElement('configurator-app')
export class ConfiguratorApp extends connect(store)(router(navigator(outlet(LitElement)))) {

	static styles = [
		colorThemes,
		fonts,
		sizes,
		css`
			:host {
				height: 100%;
			}
			.container {
				height: 100%;
				display: flex;
				flex-direction: column;
				transition: background-color 0.4s ease-in-out, color 0.4s ease-in-out;
				background-color: var(--color-black);
			}
			.embed {
				height: fit-content;
			}
			.pages {
				overflow-y: scroll;
			}

			.head {
				flex-grow: 0;
				z-index: 10;
				height: var(--header-height);
			}
			.head.specsheet {
				height: 0;
			}
			.content {
				flex-grow: 1;
				align-self: stretch;
				position: relative;
				height: 100%;
				z-index: 1;
			}
		`
	];

	// observed attribute name is "myvalue"
	static properties = {
		api: {type: String},
	};

	@property({type: String})
	host = '';

	@property({type: String})
	base = '';

	@property({type: String})
	express = '/express';

	@property({type: String})
	auth = '/users/userManagement.php';

	@property({type: String})
	engine = '';

	@property({type: String})
	engine2 = '';

	@property({type: String})
	pim = '';

	@property({type: String})
	pvgis = '';

	@property({type: String})
	specsheet = '';

	@property({type: String})
	specsheetType = 'detail';

	@property({type: String})
	shortnumber = '';

	@property({type: String})
	assets = '/ePIM';

	@property({type: String})
	channel = '';

	@property({type: String})
	features = '';

	@property({type: String})
	route = '';

	@property({type: Object})
	params = {};

	@property({type: Object})
	query = {};


	//fixed values for embedding
	@property({type: String})
	embed = 'false';

	@property({type: String})
	family = '';

	@property({type: String})
	mode = '';

	@property({type: String})
	lang = '';

	@property({type: String})
	region = '';

	@property({type: String})
	productImages = null;

	@property({type: String})
	loginPage = 'false';

	configurationAvailable = false;

	static get routes() {
		return getRoutes();
	}

	constructor() {
		super();

		setLocale('de');

		this.localStorage = storeEngine.createStore(
			[localStorage, cookieStorage],
			[defaultsPlugin]
		);
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.specsheetActive = state.status.specsheetActive;
		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.sectionTheme = selectCurrentSectionConfig(state)?.theme ?? '';

		// override Theme based on selected environment (Iomo)
		if (selectFamily(state) === 'Iomo') {
			this.sectionTheme = selectIomoEnvironment(state)=== 'day' ? 'lightTheme' : 'darkTheme';
		}
		this.finalized = selectFinalized(state);
	}

	router(route, params, query, data) {
		if(this.embed !== 'true') {
			// console.log('router', route, params, query, data);
			this.route = route;
			this.params = params;
			this.query = query;
			if (route !== 'prices') {
				this.setStatusFromRoute();
			}
		}
	}
	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		} else {
			return false;
		}
	}
	setStatusFromRoute() {
		const status = store.getState().status;
		console.log('setStatusFromRoute', status);
		if (status) {
			if (this.api && this.api !== status.apiUrl) {
				store.dispatch(setStatus(
					{
						host: this.host,
						baseUrl: this.base,
						apiUrl: this.api,
						expressUrl: this.express,
						authUrl: this.auth,
						engineUrl: this.engine + '/engine/v2',
						engine2Url: this.engine2,
						pimUrl: this.pim + '/components/dataprovider/v1',
						priceUrl: this.pim + '/components/pricing/v1',
						shortNumberUrl: this.shortnumber + '/short-number/v1',
						shortArticleNumberUrl: this.pim + '/short-article-number/v1',
						epimAssetsUrl: this.assets,
						pvgisUrl: this.pvgis,
						specsheetUrl: this.specsheet,
						specsheetType: this.specsheetType,
						channel: this.channel,
						routeUid: this.params.uid,
						productImagesUrl: this.productImages,
						features: this.features.split(',').reduce((features, cur) => {
							features[cur] = true;
							return features;
						}, {})
					}
				));
				if (this.getCookie('slxcfgsession')) {
					store.dispatch(silentLogin({}));
				}
			}
			if (this.params.family !== status.family) {
				// console.log('family', this.params.family);
				if (this.params.family === 'LightProfile') {
					store.dispatch(setFamily({family: 'LightProfile'}));
				} else {
					store.dispatch(setFamily({family: this.params.family ?? 'Start'}));
				}
			}

			if (this.params.region !== status.region) {
				// console.log('region', this.params.region);
				store.dispatch(setRegion({region: this.params.region ?? 'int'}));
			}
			if (this.params.language !== status.lang) {
				// console.log('lang', this.params.language);
				store.dispatch(setLang({lang: this.params.language ?? 'de'}));
			}
			if (this.params.media !== status.media) {
				// console.log('media', this.params.media);
				if (this.params.media && this.params.media === 'print2') {
					store.dispatch(setLoggedIn({loggedIn: true}));
				}
				if (this.params.media && this.params.media !== 'undefined') {
					store.dispatch(setMedia({media: this.params.media}));
				} else {
					// Add any additional actions or logic for the default media value
					store.dispatch(setMedia({media: 'sections'}));
				}
			}
			if (this.params.uid !== status.dataKey) {
				//await this.loadConfiguration();
				//this.setModeFromRoute();
				//await this.navigate(routeUrl());
			}
		}
	}
	setStatusFromAttributes() {
		const status = store.getState().status;
		if (status) {
			if (this.api && this.api !== status.apiUrl) {
				store.dispatch(setStatus(
					{
						host: this.host,
						baseUrl: this.base,
						apiUrl: this.api,
						expressUrl: this.express,
						authUrl: this.auth,
						engineUrl: this.engine + '/engine/v2',
						engine2Url: this.engine2,
						pimUrl: this.pim + '/components/dataprovider/v1',
						priceUrl: this.pim + '/componets/price/v1',
						shortNumberUrl: this.shortnumber + '/short-number/v1',
						shortArticleNumberUrl: this.pim + '/short-article-number/v1',
						epimAssetsUrl: this.assets,
						pvgisUrl: this.pvgis,
						specsheetUrl: this.specsheet,
						specsheetType: this.specsheetType,
						productImagesUrl: this.productImages,
						channel: this.channel,
						mode: this.mode,
						features: this.features.split(',').reduce((features, cur) => {
							features[cur] = true;
							return features;
						}, {})
					}
				));
				if (this.getCookie('slxcfgsession')) {
					store.dispatch(silentLogin({}));
				}
			}
			if (this.route !== 'prices') {
				store.dispatch(setEmbed({embed: this.embed === 'true'}));
				store.dispatch(setFamily({family: this.family ?? 'Start'}));
				store.dispatch(setRegion({region: this.region ?? 'int'}));
				store.dispatch(setLang({lang: this.lang ?? 'de'}));
				store.dispatch(setMedia({media: 'overview'}));

				const familyConfig = selectFamilyConfig(store.getState());
				// start init process
				store.dispatch(setFeatureModel({featureModel: familyConfig.model}));

				const fixedDecisions = [
					spcExpliciteChoiceDecision('shared::ProductType::CX_ProductFamily', familyConfig.defaultProductFamily),
					spcExpliciteChoiceDecision('shared::ContextV3::CX_Region', regionToValueMap[this.region]),
				];
				console.log('family config', selectFamilyConfig(store.getState()), fixedDecisions);

				store.dispatch(startSpcSession({deploymentName: familyConfig.model, fixedDecisions: fixedDecisions, instance: 'default', skipWar: familyConfig.skipWizardAttributeRelations}));
			}
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
		addEventListener('resize', this.appHeight);

		// dx backdoor – allow prepopulation with config in embedded form
		const query = new URLSearchParams(window.location.search);
		if (query.has('__uid')) {
			this.params.uid = query.get('__uid');
			await store.dispatch(setKey({key: generateKey()}));
			this.setLastUsedKey(store.getState().status.family, this.params.uid);
			this.setStatusFromAttributes();
			await this.loadConfiguration();
			return;
		}

		if(this.route === 'prices' || this.embed === 'true') {
			if (this.route !== 'prices') {
				await this.newConfiguration();
			}
			this.setStatusFromAttributes();
		} else {
			await this.loadConfiguration();
			// only init spc when already finalized
			store.dispatch(setInitialized({initialized: true}));
			const familyConfig = selectFamilyConfig(store.getState());
			const fixedDecisions = [
				spcExpliciteChoiceDecision('shared::ProductType::CX_ProductFamily', familyConfig.defaultProductFamily),
			];
			console.log('family config', selectFamilyConfig(store.getState()), fixedDecisions);
			store.dispatch(startSpcSession({deploymentName: familyConfig.model, fixedDecisions: fixedDecisions, instance: 'default', skipWar: familyConfig.skipWizardAttributeRelations}));
			this.navigate(routeUrl());
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeEventListener('keydown', this.handleKeydown);
		removeEventListener('resize', this.appHeight);
	}

	appHeight() {
		document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 68: // d
				if (e.ctrlKey) {
					this.toggleDebug();
				}
				break;
			case 86: // v
				if (e.ctrlKey) {
					this.toggleDebugShowAll();
				}
				break;
			case 65: // a
				if (e.ctrlKey && e.shiftKey) {
					this.toggleDebugAeto();
				}
				break;
			case 67: // c
				if (e.ctrlKey) {
					this.toggleComponentView();
				}
				break;
			case 73: // i
				if (e.ctrlKey) {
					//this.toggleModeSwitchActive();
				}
				break;
		}
	};

	toggleDebug(e) {
		store.dispatch(setDebug(
			{
				debug: !this.status.debug
			}
		));
	}
	toggleDebugShowAll(e) {
		store.dispatch(setDebugShowAll(
			{
				debugShowAll: !this.status.debugShowAll
			}
		));
	}
	toggleDebugAeto(e) {
		store.dispatch(setDebugAeto(
			{
				debugAeto: !this.status.debugAeto
			}
		));
	}
	toggleComponentView(e) {
		store.dispatch(setComponentView(
			{
				componentView: !this.status.componentView
			}
		));
	}

	async loadConfiguration() {
		console.log('loadConfiguration');

		// get the configuration UID from route
		const keyFromRoute = this.params.uid;
		const state = store.getState();
		if (keyFromRoute === undefined || keyFromRoute === 'new') {
			// no key, new configuration from default
			const newKey = generateKey();
			await store.dispatch(setKey({key: newKey}));
			store.dispatch(setOriginalKey({key: newKey}));
			console.log('no key, create new key: ', store.getState(), store.getState().status.dataKey);
		} else {
			// fetch the configuration
			console.log('loading existing config:', keyFromRoute);
			await store.dispatch(fetchConfiguration(keyFromRoute));

			const key = this.state.configuration.data.dataKey;
			console.log('key', key);
			await store.dispatch(setKey({key: key}));
		}
		this.configurationAvailable = true;
	}
	async newConfiguration() {
		console.log('newConfiguration', this.status.family, selectFamilyConfig(this.state));
		// start a new configuration
		const newKey = generateKey();
		await store.dispatch(setKey({key: newKey}));
		this.configurationAvailable = true;
	}

	updateUrl(baseUrl, media, key, replace = false) {
		const url = baseUrl + '/' + (media !== '' ? (media + '/') : '') + key;
		if (replace) {
			window.history.replaceState(null, '', url);
		} else {
			window.history.pushState(null, '', url);
		}
	}

	getLastUsedKey() {
		const savedKey = String(this.localStorage.get(config.lastUsedKeyName));
		if (savedKey.includes(':')) {
			return savedKey.split(':')[1];
		} else {
			return savedKey;
		}
	}

	getLastUsedFamily() {
		const savedKey = String(this.localStorage.get(config.lastUsedKeyName));
		if (savedKey.includes(':')) {
			return savedKey.split(':')[0];
		} else {
			return null;
		}
	}

	setLastUsedKey(modelid, key) {
		if (key && key !== 'undefined') {
			this.localStorage.set(config.lastUsedKeyName, modelid + ':' + key);
		}
	}


	render() {
		const status = this.status;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		if (this.route === 'prices') {
			return html`<price-sheet></price-sheet>`;
		}
		if (this.route === 'loginPage' || this.loginPage === 'true') {
			return html`
				<main-configurator active-route=${this.route} class="${classMap({embed: status.embed})}" id="main-configurator">
					<div class="container ${this.sectionTheme}">
						<login-page></login-page>
					</div>
				</main-configurator>`;
		}
		return html`
			<main-configurator active-route=${this.route} class="${classMap({embed: status.embed})}" id="main-configurator">
				${when(status.media === 'print' || status.media === 'print2',
					() => html`
						<shared-specsheet type="${this.specsheetType}"></shared-specsheet>
					`,
					() => html`
						<div class="container ${this.sectionTheme} ${this.currentSection}">
							${when(!status.embed, () => html`
								<div class="head ${this.sectionTheme} ${this.currentSection}">
									<sections-head></sections-head>
								</div>
							`)}
							${when(this.embed === 'true', () => html`
								<download-panel></download-panel>
							`)}
							<div class="content ${this.sectionTheme}">
								${choose(this.params.media, [
										['specsheet',
											() => html`
												<specsheet-panel></specsheet-panel>
											`],
										['summary',
											() => html`
												<summary-panel></summary-panel>
											`],
									],
									() => html`
										<configuration-sections .dashboardActive=${this.params.media === 'overview'}></configuration-sections>
									`
								)}
							</div>
							<modal-view></modal-view>
							<explain-view></explain-view>
							<version-notice></version-notice>
							<dev-label></dev-label>
							<solar-assembly-debug></solar-assembly-debug>
							<line-steel-label></line-steel-label>
						</div>
					`)}
			</main-configurator>

		`;
	}
}
